import { X, CheckCircle, AlertCircle, Plus, RefreshCw, Trash2 } from 'lucide-react';

interface ImportSummary {
    procedures: { add: number; update: number; skip: number };
    sections: { add: number; update: number; skip: number };
    costItems: { add: number; update: number; skip: number };
    pricing: { create: number; update: number; delete: number; blankRowsSkipped: number };
}

interface FieldChange {
    field: string;
    oldValue: any;
    newValue: any;
}

interface ImportChanges {
    procedures: Array<{ action: string; name: string; changes?: FieldChange[] }>;
    sections: Array<{ action: string; name: string; changes?: FieldChange[] }>;
    costItems: Array<{ action: string; name: string; sectionName?: string; changes?: FieldChange[] }>;
    pricing: Array<{
        action: string;
        procedureName?: string;
        sectionName?: string;
        costItemName?: string;
        changes?: FieldChange[];
    }>;
}

interface ImportPreviewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    summary: ImportSummary | null;
    changes: ImportChanges | null;
    isApplying: boolean;
}

export function ImportPreviewDialog({
    isOpen,
    onClose,
    onConfirm,
    summary,
    changes,
    isApplying,
}: ImportPreviewDialogProps) {
    if (!isOpen || !summary || !changes) return null;

    const totalChanges =
        (summary.procedures.add + summary.procedures.update) +
        (summary.sections.add + summary.sections.update) +
        (summary.costItems.add + summary.costItems.update) +
        (summary.pricing.create + summary.pricing.update + summary.pricing.delete);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-slate-900">Import Preview</h2>
                        <p className="text-sm text-slate-600 mt-1">
                            Review changes before applying to database
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 p-2 rounded hover:bg-slate-100 transition-colors"
                        disabled={isApplying}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 flex-1 overflow-y-auto">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <SummaryCard
                            title="Procedures"
                            add={summary.procedures.add}
                            update={summary.procedures.update}
                            skip={summary.procedures.skip}
                        />
                        <SummaryCard
                            title="Sections"
                            add={summary.sections.add}
                            update={summary.sections.update}
                            skip={summary.sections.skip}
                        />
                        <SummaryCard
                            title="Cost Items"
                            add={summary.costItems.add}
                            update={summary.costItems.update}
                            skip={summary.costItems.skip}
                        />
                        <SummaryCard
                            title="Pricing"
                            create={summary.pricing.create}
                            update={summary.pricing.update}
                            deleteCount={summary.pricing.delete}
                        />
                    </div>

                    {/* Changes Details */}
                    {totalChanges > 0 && (
                        <div className="space-y-4">
                            <h3 className="font-medium text-slate-900">Changes to Apply</h3>

                            {/* Procedures */}
                            {(summary.procedures.add > 0 || summary.procedures.update > 0) && (
                                <ChangeSection
                                    title="Procedures"
                                    changes={changes.procedures.filter(
                                        (c) => c.action === 'ADD' || c.action === 'UPDATE'
                                    )}
                                />
                            )}

                            {/* Sections */}
                            {(summary.sections.add > 0 || summary.sections.update > 0) && (
                                <ChangeSection
                                    title="Cost Sections"
                                    changes={changes.sections.filter(
                                        (c) => c.action === 'ADD' || c.action === 'UPDATE'
                                    )}
                                />
                            )}

                            {/* Cost Items */}
                            {(summary.costItems.add > 0 || summary.costItems.update > 0) && (
                                <ChangeSection
                                    title="Cost Items"
                                    changes={changes.costItems.filter(
                                        (c) => c.action === 'ADD' || c.action === 'UPDATE'
                                    )}
                                />
                            )}

                            {/* Pricing */}
                            {(summary.pricing.create > 0 || summary.pricing.update > 0 || summary.pricing.delete > 0) && (
                                <PricingChangeSection
                                    changes={changes.pricing.filter(
                                        (c) => c.action === 'CREATE' || c.action === 'UPDATE' || c.action === 'DELETE'
                                    )}
                                />
                            )}

                            {/* Blank rows skipped message */}
                            {summary.pricing.blankRowsSkipped > 0 && (
                                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                                        <div className="text-sm text-blue-900">
                                            <strong>{summary.pricing.blankRowsSkipped}</strong> blank pricing row{summary.pricing.blankRowsSkipped !== 1 ? 's' : ''} skipped (not applicable to procedures)
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {totalChanges === 0 && (
                        <div className="text-center py-8">
                            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                            <p className="text-slate-600">No changes detected</p>
                            <p className="text-sm text-slate-500 mt-1">
                                All data in the file already matches the database
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                        {totalChanges > 0 ? (
                            <>
                                <strong>{totalChanges}</strong> change{totalChanges !== 1 ? 's' : ''} will be applied
                            </>
                        ) : (
                            'No changes to apply'
                        )}
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="btn btn-secondary px-6 py-2"
                            disabled={isApplying}
                        >
                            Cancel
                        </button>
                        {totalChanges > 0 && (
                            <button
                                onClick={onConfirm}
                                className="btn btn-primary px-6 py-2"
                                disabled={isApplying}
                            >
                                {isApplying ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Applying...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Apply Changes
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({
    title,
    add = 0,
    update = 0,
    skip = 0,
    create = 0,
    deleteCount = 0,
}: {
    title: string;
    add?: number;
    update?: number;
    skip?: number;
    create?: number;
    deleteCount?: number;
}) {
    const totalChanges = add + update + create + deleteCount;

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-sm font-medium text-slate-600 mb-2">{title}</div>
            <div className="space-y-1">
                {(add > 0 || create > 0) && (
                    <div className="flex items-center gap-2 text-sm">
                        <Plus className="w-3 h-3 text-green-600" />
                        <span className="text-slate-700">
                            {add || create} new
                        </span>
                    </div>
                )}
                {update > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                        <RefreshCw className="w-3 h-3 text-blue-600" />
                        <span className="text-slate-700">{update} update{update !== 1 ? 's' : ''}</span>
                    </div>
                )}
                {deleteCount > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                        <Trash2 className="w-3 h-3 text-red-600" />
                        <span className="text-slate-700">{deleteCount} delete{deleteCount !== 1 ? 's' : ''}</span>
                    </div>
                )}
                {skip > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400">{skip} skip{skip !== 1 ? 'ped' : 'ped'}</span>
                    </div>
                )}
                {totalChanges === 0 && skip === 0 && (
                    <div className="text-sm text-slate-400">No changes</div>
                )}
            </div>
        </div>
    );
}

function PricingChangeSection({
    changes,
}: {
    changes: Array<{
        action: string;
        procedureName?: string;
        sectionName?: string;
        costItemName?: string;
        changes?: FieldChange[];
    }>;
}) {
    if (changes.length === 0) return null;

    // Group changes by procedure
    const changesByProcedure = changes.reduce((acc, change) => {
        const procedureName = change.procedureName || 'Unknown Procedure';
        if (!acc[procedureName]) {
            acc[procedureName] = [];
        }
        acc[procedureName].push(change);
        return acc;
    }, {} as Record<string, typeof changes>);

    const formatFieldName = (field: string): string => {
        return field
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const formatValue = (value: any): string => {
        if (value === null || value === undefined) return '(empty)';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') return value.toString();
        return String(value);
    };

    return (
        <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-medium text-slate-900 mb-3">Pricing Updates</h4>
            <div className="space-y-4">
                {Object.entries(changesByProcedure).map(([procedureName, procedureChanges]) => (
                    <div key={procedureName} className="bg-white rounded-lg p-3 border border-slate-200">
                        <div className="font-semibold text-slate-800 mb-2 pb-2 border-b border-slate-200">
                            {procedureName}
                        </div>
                        <div className="space-y-2">
                            {procedureChanges.map((change, index) => (
                                <div key={index} className="pl-3 border-l-2 border-slate-200">
                                    <div className="flex items-start gap-2">
                                        {change.action === 'CREATE' ? (
                                            <Plus className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" />
                                        ) : change.action === 'DELETE' ? (
                                            <Trash2 className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
                                        ) : (
                                            <RefreshCw className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-slate-700">
                                                {change.sectionName} → {change.costItemName}
                                            </div>
                                            <span className={`text-xs uppercase tracking-wide ${
                                                change.action === 'DELETE' ? 'text-red-500' :
                                                change.action === 'CREATE' ? 'text-green-600' : 'text-blue-600'
                                            }`}>
                                                {change.action}
                                            </span>
                                            {change.changes && change.changes.length > 0 && (
                                                <div className="mt-1 space-y-0.5">
                                                    {change.changes.map((fieldChange, idx) => (
                                                        <div key={idx} className="flex items-start gap-2 text-xs">
                                                            <span className="text-slate-600 min-w-[80px]">
                                                                {formatFieldName(fieldChange.field)}:
                                                            </span>
                                                            <span className="text-red-600 line-through">
                                                                {formatValue(fieldChange.oldValue)}
                                                            </span>
                                                            <span className="text-slate-400">→</span>
                                                            <span className="text-green-600 font-medium">
                                                                {formatValue(fieldChange.newValue)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ChangeSection({
    title,
    changes,
}: {
    title: string;
    changes: Array<{
        action: string;
        name?: string;
        sectionName?: string;
        procedureName?: string;
        costItemName?: string;
        changes?: FieldChange[];
    }>;
}) {
    if (changes.length === 0) return null;

    const formatFieldName = (field: string): string => {
        return field
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const formatValue = (value: any): string => {
        if (value === null || value === undefined) return '(empty)';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') return value.toString();
        return String(value);
    };

    return (
        <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-medium text-slate-900 mb-3">{title}</h4>
            <div className="space-y-3">
                {changes.map((change, index) => (
                    <div key={index} className="bg-white rounded-lg p-3 border border-slate-200">
                        <div className="flex items-center gap-3 mb-2">
                            {change.action === 'ADD' || change.action === 'CREATE' ? (
                                <Plus className="w-4 h-4 text-green-600 shrink-0" />
                            ) : change.action === 'DELETE' ? (
                                <Trash2 className="w-4 h-4 text-red-600 shrink-0" />
                            ) : (
                                <RefreshCw className="w-4 h-4 text-blue-600 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                {change.procedureName ? (
                                    <>
                                        <div className="font-medium text-slate-700">
                                            {change.procedureName}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {change.sectionName} → {change.costItemName}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="font-medium text-slate-700">
                                            {change.name || change.costItemName || 'Item'}
                                        </div>
                                        {change.sectionName && (
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                Section: {change.sectionName}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <span className={`text-xs uppercase tracking-wide ${
                                change.action === 'DELETE' ? 'text-red-500' : 'text-slate-400'
                            }`}>
                                {change.action}
                            </span>
                        </div>

                        {change.changes && change.changes.length > 0 && (
                            <div className="mt-2 space-y-1 text-xs">
                                {change.changes.map((fieldChange, idx) => (
                                    <div key={idx} className="flex items-start gap-2 py-1">
                                        <span className="text-slate-600 min-w-[100px]">
                                            {formatFieldName(fieldChange.field)}:
                                        </span>
                                        <span className="text-red-600 line-through">
                                            {formatValue(fieldChange.oldValue)}
                                        </span>
                                        <span className="text-slate-400">→</span>
                                        <span className="text-green-600 font-medium">
                                            {formatValue(fieldChange.newValue)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
